import Vue from 'vue';

import playerService from '../../services/player.service';
import authService from '../../services/authentication.service';
import utils from '../../utils';

import { SET, UNSET, SET_BALANCE, SET_PASSWORDS, UPDATE_STATE, SET_WINNING_JACKPOT, SET_SKIP_UPDATES } from './mutation-types';

// watch player state
const stateUpdatePeriod = 2000; // ms
let isWatchingState = false;
let isPausingState = false;
let pushMessageSubscribers = {};
let receivedPushMessages = [];

const updateStateFunc = async function(state) {
    try {
        if (isWatchingState && state.player.isLoggedIn() && !state.player.shouldResetPassword()) {
            const newState = await playerService.getState();
            state.player.setPasswords(newState.branchParameters);
            state.player.setBalance(newState.balance);
            state.player.updateState(newState);

            if (Object.keys(pushMessageSubscribers).length) {
                newState.notReadMessages.forEach(message => {
                    if (receivedPushMessages.indexOf(message.id) === -1) {
                        receivedPushMessages.push(message.id);
                        for (const uuid in pushMessageSubscribers) {
                            pushMessageSubscribers[uuid](message);
                        }
                    }
                });
            }
        }
    } catch (e) {
        console.error("player state receiving error");
        console.error(e);
    }
};

const watchStateFunc = async function(state) {
    
    if (isPausingState) {
        setTimeout(() => watchStateFunc(state), stateUpdatePeriod);
        return;
    }

    await updateStateFunc(state);
    if (isWatchingState) setTimeout(() => watchStateFunc(state), stateUpdatePeriod);
};

// check if player inactive
// watch mouse move
let trackFrom = new Date().valueOf();
window.addEventListener('mousemove', () => {
    trackFrom = new Date().valueOf();
});
window.addEventListener('keydown', () => {
    trackFrom = new Date().valueOf();
});
// check activity
const watchActivityFunc = async function(state) {
    try {
        if (state.player.trackPlayerActivity) {
            let playerActivityPeriod = new Date().valueOf() - trackFrom;
            if (state.player.isLoggedIn() && state.player.get().lastAccessPeriodMs) {
                playerActivityPeriod = Math.min(playerActivityPeriod, state.player.get().lastAccessPeriodMs);
            }
            if (playerActivityPeriod > state.player.inactivityTimeout) {
                Vue.router.push(state.player.inactivityRoute);
                await Vue.state.player.logout();
            }
        }
    } catch (e) {
        console.error('player activity tracking error');
        console.error(e);
    }
    if (state.player.trackPlayerActivity) setTimeout(() => watchActivityFunc(state), stateUpdatePeriod);
};

let firstPlayerPromise = false;

export default function(appState, store) {
    let playerAppState = {
        skipUpdates: () => isPausingState,
        get: () => store.getters.player,
        isLoggedIn: () => !!(store.getters.player && store.getters.player.username),
        shouldResetPassword: () => store.getters.player && store.getters.player.resetPassword,
        ensure: async () => {
            if (!firstPlayerPromise) {
                await playerAppState.reload();
            }
            return firstPlayerPromise;
        },
        ensureWatchingActivity: async () => {
            if (playerAppState.trackPlayerActivity) watchActivityFunc({ player: playerAppState });
        },
        reload: async () => {
            let player = null;
            if (!firstPlayerPromise) {
                firstPlayerPromise = playerService.me();
                player = await firstPlayerPromise;

                watchActivityFunc({ player: playerAppState });
            } else {
                player = await playerService.me();
            }
            if (player && player.username) {
                store.dispatch(SET, player);
                await updateStateFunc(appState);
                await playerAppState.watchState();
            } else {
                store.dispatch(UNSET);
                playerAppState.stopWatchState();
            }
        },
        update: async () => {
            await updateStateFunc(appState);
        },
        setBalance: balance => {
            store.dispatch(SET_BALANCE, balance);
        },
        setSkipUpdates: skipUpdates => {
            isPausingState = skipUpdates;
        },
        setPasswords: params => {
            store.dispatch(SET_PASSWORDS, params);
        },
        updateState: playerState => {
            store.dispatch(UPDATE_STATE, playerState);
        },
        logout: async () => {
            playerAppState.stopWatchState();
            await authService.logout();
            store.dispatch(UNSET);
        },
        watchState: async () => {
            if (!isWatchingState) {
                isWatchingState = true;
                await watchStateFunc(appState);
            }
        },
        stopWatchState: () => {
            isWatchingState = false;
        },
        subscribePushMessages: action => {
            const uuid = utils.uuid();
            pushMessageSubscribers[uuid] = action;
            return uuid;
        },
        unsubscribePushMessages: uuid => {
            if (uuid) delete pushMessageSubscribers[uuid];
        },
        trackPlayerActivity: false,
        inactivityTimeout: 5 * 60 * 1000, // 5 min
        inactivityRoute: 'login',
        unsetWinningJackpot() {
            store.dispatch(SET_WINNING_JACKPOT, null);
        }
    };

    appState.player = playerAppState;
};



// WEBPACK FOOTER //
// ./_common/data/player/functions.js